/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, FlexCol, SecurityHelper, gaReportEvent, useFlags } from 'sarsaparilla';
import axios from 'axios';
import { gaReportingCartReservationsForStepper } from '../../utils/gaCommon';
import {
    clearBuyNowCheckoutMarker,
    setBuyNowCheckoutMarker,
} from '../../utils/buyNowTracking';
import { useBuyNowContext } from '../OrderDetailsLayout/BuyNowContext';
import BuyNowModal from './BuyNowModal';
import { isBuyNowLocationExcluded } from './buyNowLocationExclusion';

async function fetchCart() {
    return axios
        .get(`${process.env.API}/cart/shoppingcart`, {
            headers: { Authorization: SecurityHelper.getAuthHeader() },
        })
        .then((response) => response.data)
        .catch(() => null);
}

export function setShoppingCart(cartData) {
    return { type: 'GET_CART', payload: cartData };
}

function getCartReservationCount(cart) {
    const reservations = cart?.reservations?.length || 0;
    const untimedReservations = cart?.untimed_reservations?.length || 0;
    return reservations + untimedReservations;
}

function getCartReservation(cart) {
    if (getCartReservationCount(cart) !== 1) return null;
    return cart?.reservations?.[0] || cart?.untimed_reservations?.[0] || null;
}

export default function BuyNowButton({
    onProceedToCartClick,
    isDisabled,
    shouldFitContainer,
    flexColSize,
    className,
}) {
    const { reservationId, canBuyNow, locationId } = useBuyNowContext();
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalErrors, setModalErrors] = useState(null);
    const [cartData, setCartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const { buyNowLocationExclusion } = useFlags();

    const cart = useSelector((state) => state.cart);
    const isModification = useSelector((state) => state.cart?.is_modification);

    const cartReservation = getCartReservation(cart);
    const cartMismatch =
        reservationId &&
        getCartReservationCount(cart) === 1 &&
        cartReservation?.reservation_id !== reservationId;

    // Prefer the cart reservation's location; fall back to the host-supplied
    // location from BuyNowContext when the cart has no reservation yet.
    const currentLocationId = cartReservation?.order_details?.location_id || locationId;
    const locationExcluded = isBuyNowLocationExcluded(
        buyNowLocationExclusion,
        currentLocationId
    );

    const shouldHide =
        !canBuyNow ||
        getCartReservationCount(cart) > 1 ||
        isModification ||
        cartMismatch ||
        locationExcluded;

    useEffect(() => {
        if (getCartReservationCount(cart) === 0) {
            clearBuyNowCheckoutMarker();
        }
    }, [cart]);

    const handleBuyNowClick = async () => {
        setIsLoading(true);
        setModalErrors(null);

        try {
            if (onProceedToCartClick) {
                const result = await onProceedToCartClick({ navigate: false });
                if (result !== true) {
                    setIsLoading(false);
                    return;
                }
            }

            const fetchedCart = await fetchCart();
            if (!fetchedCart) {
                setIsLoading(false);
                return;
            }
            dispatch(setShoppingCart(fetchedCart));
            setCartData(fetchedCart);

            if (getCartReservation(fetchedCart)?.errors?.length) {
                setIsLoading(false);
                return;
            }

            setBuyNowCheckoutMarker();

            const viewCartEvent = await gaReportingCartReservationsForStepper(
                fetchedCart.reservations,
                {},
                1
            );
            if (viewCartEvent) {
                gaReportEvent({ event: viewCartEvent });
            }
        } catch {
            setIsLoading(false);
            return;
        }

        setIsLoading(false);
        setIsModalOpen(true);
    };

    const handleModalDismiss = () => {
        clearBuyNowCheckoutMarker();
        setIsModalOpen(false);
    };

    const handleModalComplete = () => {
        setIsModalOpen(false);
    };

    if (SecurityHelper.isMobileApp()) {
        return null;
    }

    if (shouldHide && !isModalOpen && !isLoading) {
        return null;
    }

    const content = (
        <>
            {!shouldHide && (
                <Button
                    appearance="tertiary"
                    size="lg"
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onClick={handleBuyNowClick}
                    gaTrackingId="681764723261"
                    shouldFitContainer={shouldFitContainer}
                >
                    Buy Now
                </Button>
            )}
            <BuyNowModal
                isOpen={isModalOpen}
                closeModal={handleModalDismiss}
                onComplete={handleModalComplete}
                errors={modalErrors}
                cartData={cartData}
            />
        </>
    );

    if (flexColSize) {
        return (
            <FlexCol xs={flexColSize} className={className}>
                {content}
            </FlexCol>
        );
    }

    return content;
}

BuyNowButton.propTypes = {
    onProceedToCartClick: PropTypes.func,
    isDisabled: PropTypes.bool,
    shouldFitContainer: PropTypes.bool,
    flexColSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
};

// cSpell:ignore shoppingcart, untimed_reservations, untimed
